/**
 * New-user onboarding gate (skippable).
 *
 * Wraps the protected `/app` subtree. Once `whoami` resolves, a user who
 * has not finished — or skipped to the end of — the onboarding funnel is
 * redirected into `/onboarding/experience`. Finishing OR skipping the
 * source step marks the user onboarded server-side (and invalidates the
 * `whoami` cache), so the gate then lets them through — that's what makes
 * it "skippable" rather than mandatory.
 *
 * Pre-existing accounts were backfilled as onboarded by migration ob001,
 * so the gate only ever fires for accounts created after this shipped.
 *
 * While identity is still resolving we render the children (the shell has
 * its own loading states) and, on a whoami error, we deliberately do NOT
 * redirect — the auth layer owns the un-authenticated case, and trapping
 * the user in a redirect loop would be worse than letting them through.
 */
import { Navigate } from 'react-router';

import { paths } from '@/config/paths';
import { useWhoami } from '@/features/auth/hooks/use-whoami';

import type { ReactNode } from 'react';

export function OnboardingGate({ children }: { children: ReactNode }) {
  const { data, isLoading, isError } = useWhoami();

  if (!isLoading && !isError && data && !data.is_onboarded) {
    return <Navigate to={paths.onboardingExperience} replace />;
  }

  return <>{children}</>;
}
